import React, { useState, useEffect } from "react"
import { ListGroup, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { getDevice } from "@src/api"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { usePartner } from "@src/partner"
import { useFleets } from "@src/settings"

type DeviceSearchProps = {
  searchText: string
  onClick: (device: SLIM_DEVICE_TYPE) => void
}

const highlightSearchText = (text: string, searchText: string): React.ReactNode => {
  if (!searchText) return text

  const escapedSearchText = searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const regex = new RegExp(`(${escapedSearchText})`, "gi")
  const parts = text.split(regex)

  return parts.map((part, index) => {
    const matchRegex = new RegExp(`^${escapedSearchText}$`, "i")
    return matchRegex.test(part) ? <strong key={`highlight-${index}`}>{part}</strong> : part
  })
}

export const DeviceSearch: React.FC<DeviceSearchProps> = ({ searchText, onClick }) => {
  const { t } = useTranslation()
  const { partner } = usePartner()

  const [loading, setLoading] = useState<boolean>(false)
  const [results, setResults] = useState<SLIM_DEVICE_TYPE[]>([])
  const { fleet } = useFleets()

  useEffect(() => {
    setLoading(true)
    getDevice(partner?.uuid, fleet && fleet.uuid, { search_string: searchText }).then(
      ({ response, data }) => {
        if (response.ok) setResults(data.devices)
        setLoading(false)
      },
    )
  }, [searchText, partner])

  if (loading)
    return (
      <div className="d-flex justify-content-center">
        <Spinner size="sm" animation="border" />
      </div>
    )
  if (results.length == 0)
    return (
      <ul className="list-group">
        <li className="list-group-item">{t("sidebar.no_device_found")}</li>
      </ul>
    )
  return (
    <ListGroup defaultActiveKey="#link1">
      {results.map((result) => {
        const displayParts = [
          highlightSearchText(result.serialnumber, searchText),
          " - ",
          highlightSearchText(result.name, searchText),
        ]

        if (result.id_number)
          displayParts.push(" - ", highlightSearchText(result.id_number, searchText))

        return (
          <ListGroup.Item action onClick={() => onClick(result)} key={result.serialnumber}>
            {displayParts}
          </ListGroup.Item>
        )
      })}
    </ListGroup>
  )
}
